import { SignatureV4 } from "@aws-sdk/signature-v4";
import { Sha256 } from "@aws-crypto/sha256-browser";
import { HttpRequest } from "@aws-sdk/protocol-http";
import { API, Auth } from "aws-amplify";

/**
 * POST a JSON body to an AWS Lambda Function URL using SigV4 (IAM) signing.
 * Both the bulk image-generation pipeline and single-image regeneration use
 * IAM-authed Function URLs, so the request must be signed with the caller's
 * Cognito credentials. Returns the parsed JSON response (unwrapping the common
 * `{ body: "<json string>" }` Lambda-proxy envelope when present).
 */
export const signedLambdaFetch = async (
  url: string,
  body: Record<string, any>
): Promise<{ ok: boolean; status: number; data: any }> => {
  const lambdaUrl = new URL(url);
  const credentials = await Auth.currentCredentials();
  const requestBody = JSON.stringify(body);

  const request = new HttpRequest({
    method: "POST",
    hostname: lambdaUrl.hostname,
    path: lambdaUrl.pathname,
    headers: {
      "Content-Type": "application/json",
      host: lambdaUrl.hostname,
    },
    body: requestBody,
  });

  const signer = new SignatureV4({
    credentials,
    region: process.env.NEXT_PUBLIC_AWS_REGION || "eu-central-1",
    service: "lambda",
    sha256: Sha256,
  });

  const signedRequest = await signer.sign(request);
  // Drop the `host` header — fetch sets it and browsers reject it as unsafe.
  const { host, ...headersForRequest } = signedRequest.headers;

  const res = await fetch(url, {
    method: "POST",
    headers: headersForRequest,
    body: requestBody,
  });

  const json = await res.json();
  const data =
    typeof json.body === "string"
      ? JSON.parse(json.body)
      : json.body || json;

  return { ok: res.ok, status: res.status, data };
};

/**
 * GET a JSON resource from an IAM-authed Lambda Function URL (SigV4-signed).
 */
export const signedLambdaGet = async (
  url: string
): Promise<{ ok: boolean; status: number; data: any }> => {
  const lambdaUrl = new URL(url);
  const credentials = await Auth.currentCredentials();

  const request = new HttpRequest({
    method: "GET",
    hostname: lambdaUrl.hostname,
    path: lambdaUrl.pathname,
    headers: {
      host: lambdaUrl.hostname,
    },
  });

  const signer = new SignatureV4({
    credentials,
    region: process.env.NEXT_PUBLIC_AWS_REGION || "eu-central-1",
    service: "lambda",
    sha256: Sha256,
  });

  const signedRequest = await signer.sign(request);
  const { host, ...headersForRequest } = signedRequest.headers;

  const res = await fetch(url, {
    method: "GET",
    headers: headersForRequest,
  });

  const json = await res.json();
  const data =
    typeof json.body === "string"
      ? JSON.parse(json.body)
      : json.body || json;

  return { ok: res.ok, status: res.status, data };
};

export interface RendererRatio {
  value: string;
  /** The shape the ACTIVE renderer will really produce for this ratio. */
  renders_as: string;
  exact: boolean;
}

export interface RendererConfig {
  renderer: string;
  ratios: RendererRatio[];
}

let rendererConfigCache: { value: RendererConfig; expires: number } | null = null;

/**
 * Fetch which image renderer the backend is currently using (Gemini vs
 * gpt-image A/B flag in SSM) and what shape each aspect ratio really renders
 * at on that arm. GET on the regenerate Function URL. Cached for a minute —
 * matching the backend's own flag TTL. Returns null when unavailable so the
 * dropdowns degrade to their static lists.
 */
export const fetchRendererConfig = async (): Promise<RendererConfig | null> => {
  const url = process.env.NEXT_PUBLIC_IMAGE_REGENERATION_URL;
  if (!url) return null;

  const now = Date.now();
  if (rendererConfigCache && now < rendererConfigCache.expires) {
    return rendererConfigCache.value;
  }

  try {
    const { ok, data } = await signedLambdaGet(url);
    if (!ok || !data?.renderer || !Array.isArray(data?.ratios)) return null;
    rendererConfigCache = { value: data, expires: now + 60_000 };
    return data;
  } catch {
    return null;
  }
};

export interface ProjectImageSettings {
  guidelines: string;
  coverAspectRatio: string;
}

/**
 * Fetch the image-generation settings (brand style guidelines + default cover
 * aspect ratio) of the project that owns a brief/article. Brand guidelines are
 * required: generation fails visibly when the article has no project, the
 * settings request fails, or the project has no saved guidelines.
 */
export const fetchProjectImageSettings = async (
  articleId: string
): Promise<ProjectImageSettings> => {
  const briefData = await API.get("brief", `/briefs/object/${articleId}`, {});
  if (!briefData?.projectId) {
    throw new Error(
      "This article is not linked to a project. Brand image guidelines are required to generate images."
    );
  }

  const settingsRes = await API.get(
    "projects",
    `/projects/getBrandStyleGuidelines/${briefData.projectId}`,
    {}
  );
  if (!settingsRes?.success) {
    throw new Error(
      settingsRes?.error || "Could not load this project's image settings."
    );
  }

  const guidelines =
    typeof settingsRes.guidelines === "string"
      ? settingsRes.guidelines.trim()
      : "";
  if (!guidelines) {
    throw new Error(
      "Brand image guidelines are not configured for this project."
    );
  }

  return {
    guidelines,
    coverAspectRatio: settingsRes.coverImageAspectRatio || "16:9",
  };
};

export interface RegenerateImageParams {
  articleId: string;
  opportunity: Record<string, any>;
  userFeedback: string;
  /** URL of the current image — sent to Gemini as content so the
   * regeneration adjusts it instead of generating from scratch. */
  previousImageUrl?: string;
}

export interface RegenerateImageResult {
  imageUrl: string;
  metadata: Record<string, any>;
}

/**
 * Regenerate a single image from its original generation "opportunity" plus a
 * free-text user comment ("what I didn't like / what I want"). Calls the
 * regenerate Lambda Function URL and returns the new image URL + refreshed
 * opportunity metadata to persist back on the editor node.
 *
 * Brand guidelines are deliberately not sent: the opportunity's
 * brand_execution_contract already carries the selected brand rules, and the
 * regenerate Lambda ignores the full guidelines document.
 */
export const regenerateImage = async ({
  articleId,
  opportunity,
  userFeedback,
  previousImageUrl,
}: RegenerateImageParams): Promise<RegenerateImageResult> => {
  const url = process.env.NEXT_PUBLIC_IMAGE_REGENERATION_URL;
  if (!url) {
    throw new Error("Image regeneration is not configured");
  }

  const { ok, data } = await signedLambdaFetch(url, {
    article_id: articleId,
    article_url: `article-${articleId}`,
    opportunity,
    user_feedback: userFeedback,
    previous_image_url: previousImageUrl || "",
  });

  if (!ok || !data?.success || !data?.image_url) {
    throw new Error(data?.error || "Image regeneration failed");
  }

  return {
    imageUrl: data.image_url,
    metadata: data.metadata || opportunity,
  };
};
